import { useEffect, useRef, useState, useCallback } from 'react';
import * as PIXI from 'pixi.js';
import { usePixiApp } from '../../services/pixiApp/pixiAppProvider';
import VsScreenManager from '../../features/gameplay/managers/vsScreenManager';
import { getProxiedCardImageUrl } from '../../features/gameplay/utils/commonUtils';

import './style.css';

const MOCK_USER_ID = 'demo-player-1';
const MOCK_OPPONENT_ID = 'demo-player-2';
const MOCK_ROOM_NAME = 'TEST-ROOM';

const MOCK_DECKS = [
    { id: 'demo-1', name: 'Straw Hat Crew', leader: 'ST01-001' },
    { id: 'demo-2', name: 'Animal Kingdom', leader: 'ST04-001' },
    { id: 'demo-3', name: 'The Seven Warlords', leader: 'ST03-001' },
];

const OPPONENT_LEADER = { imgUrl: 'ST04-001.png' };

async function loadCardImages(leaders) {
    await PIXI.Assets.load(leaders.map(leader => ({ src: getProxiedCardImageUrl(`${leader}.png`) })));
}

export default function VsScreenDemoPage() {
    const { app, setAutoResize } = usePixiApp();
    const pixiContainerRef = useRef(null);
    const managerRef = useRef(null);

    const [isFindMatchMode, setIsFindMatchMode] = useState(true);
    const [playerName, setPlayerName] = useState('Player One');
    const [opponentName, setOpponentName] = useState('Player Two');
    const [hasOpponent, setHasOpponent] = useState(false);
    const [playerReady, setPlayerReady] = useState(true);
    const [opponentReady, setOpponentReady] = useState(false);
    const [deckIndex, setDeckIndex] = useState(0);
    const [isReady, setIsReady] = useState(false);
    const [isAutoResize, setIsAutoResize] = useState(true);

    const selectedDeck = MOCK_DECKS[deckIndex];

    const buildRoomState = useCallback((overrides = {}) => {
        const users = [
            { userId: MOCK_USER_ID, username: playerName, readyToPlay: playerReady },
        ];

        if (hasOpponent) {
            users.push({ userId: MOCK_OPPONENT_ID, username: opponentName, readyToPlay: opponentReady });
        }

        return { users, ...overrides };
    }, [playerName, opponentName, hasOpponent, playerReady, opponentReady]);

    const wireCallbacks = useCallback((manager) => {
        manager.onSelectDeckButtonClicked = () => {
            const nextIndex = (deckIndex + 1) % MOCK_DECKS.length;
            setDeckIndex(nextIndex);
            manager.changeDeck(MOCK_DECKS[nextIndex]);
        };
        manager.onReadyButtonClicked = () => setPlayerReady(true);
        manager.onCancelButtonClicked = () => setPlayerReady(false);
        manager.onBackButtonClicked = () => console.log('Back clicked');
        manager.onCopyRoomNameButtonClicked = () => navigator.clipboard.writeText(MOCK_ROOM_NAME);
    }, [deckIndex]);

    const initVsScreen = useCallback(async () => {
        if (!app || !pixiContainerRef.current) return;

        if (managerRef.current) {
            managerRef.current.detachFromPixiApp(app);
            managerRef.current = null;
        }

        await VsScreenManager.loadAssets();
        await loadCardImages([selectedDeck.leader, 'ST04-001']);

        const manager = new VsScreenManager(isFindMatchMode, MOCK_ROOM_NAME, app.screen.width, app.screen.height);
        manager.attachToPixiApp(app);
        manager.buildVsScreen(
            playerName,
            selectedDeck,
            hasOpponent ? opponentName : '',
            hasOpponent && opponentReady,
        );
        wireCallbacks(manager);

        if (!pixiContainerRef.current.contains(app.canvas)) {
            pixiContainerRef.current.appendChild(app.canvas);
        }

        managerRef.current = manager;
        setIsReady(true);
    }, [app, isFindMatchMode, playerName, selectedDeck, hasOpponent, opponentName, opponentReady, wireCallbacks]);

    useEffect(() => {
        if (!app) return;

        setAutoResize(isAutoResize);
    }, [app, isAutoResize, setAutoResize]);

    useEffect(() => {
        initVsScreen();

        return () => {
            if (managerRef.current && app) {
                managerRef.current.detachFromPixiApp(app);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [app, isFindMatchMode]);

    useEffect(() => {
        if (!app) return;

        const onAppResize = () => {
            if (managerRef.current) {
                managerRef.current.resize(app.screen.width, app.screen.height);
                managerRef.current.updateRoomState(MOCK_USER_ID, buildRoomState());
            }
        };

        window.addEventListener('appResize', onAppResize);

        return () => {
            window.removeEventListener('appResize', onAppResize);
        };
    }, [app, buildRoomState]);

    useEffect(() => {
        if (!isReady || !managerRef.current) return;
        managerRef.current.updateRoomState(MOCK_USER_ID, buildRoomState());
    }, [isReady, buildRoomState]);

    const handleRebuild = () => {
        setIsReady(false);
        initVsScreen();
    };

    const handleLeaderAnimation = async () => {
        if (!managerRef.current) return;
        await loadCardImages(['ST04-001']);
        await managerRef.current.showOpponentLeaderAnimation(OPPONENT_LEADER);
    };

    return (
        <div className="vs-screen-demo">
            <div className="vs-screen-demo__controls">
                <h1 style={{ margin: '0 0 12px', fontSize: 18 }}>VS Screen Demo</h1>

                <label style={{ display: 'block', marginBottom: 8 }}>
                    <input
                        type="checkbox"
                        checked={isAutoResize}
                        onChange={(e) => setIsAutoResize(e.target.checked)}
                    />
                    {' '}Auto resize
                </label>

                <label style={{ display: 'block', marginBottom: 8 }}>
                    <input
                        type="checkbox"
                        checked={isFindMatchMode}
                        onChange={(e) => setIsFindMatchMode(e.target.checked)}
                    />
                    {' '}Find match mode
                </label>

                <label style={{ display: 'block', marginBottom: 8 }}>
                    Player name
                    <input
                        type="text"
                        value={playerName}
                        onChange={(e) => setPlayerName(e.target.value)}
                        style={{ display: 'block', width: '100%', marginTop: 4 }}
                    />
                </label>

                <label style={{ display: 'block', marginBottom: 8 }}>
                    <input
                        type="checkbox"
                        checked={hasOpponent}
                        onChange={(e) => setHasOpponent(e.target.checked)}
                    />
                    {' '}Opponent joined
                </label>

                {hasOpponent && (
                    <label style={{ display: 'block', marginBottom: 8 }}>
                        Opponent name
                        <input
                            type="text"
                            value={opponentName}
                            onChange={(e) => setOpponentName(e.target.value)}
                            style={{ display: 'block', width: '100%', marginTop: 4 }}
                        />
                    </label>
                )}

                <label style={{ display: 'block', marginBottom: 8 }}>
                    <input
                        type="checkbox"
                        checked={playerReady}
                        onChange={(e) => setPlayerReady(e.target.checked)}
                    />
                    {' '}Player ready
                </label>

                {hasOpponent && (
                    <label style={{ display: 'block', marginBottom: 8 }}>
                        <input
                            type="checkbox"
                            checked={opponentReady}
                            onChange={(e) => setOpponentReady(e.target.checked)}
                        />
                        {' '}Opponent ready
                    </label>
                )}

                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12 }}>
                    <button type="button" onClick={handleRebuild}>Rebuild screen</button>
                    <button type="button" onClick={handleLeaderAnimation}>Play leader reveal</button>
                </div>
            </div>
            <div className="vs-screen-demo__canvas" ref={pixiContainerRef} />
        </div>
    );
}
